import { useCallback } from "react";
import { useTranslation } from "react-i18next";
import { useMutation, useQuery, useSubscription } from "@apollo/react-hooks";
import {
  MY_TIMESHEET_CREATE_PROJECT,
  MY_TIMESHEET_GET_PROJECT_LIST,
  MY_TIMESHEET_PROJECTS_SUBSCRIPTION,
  MY_TIMESHEET_UPDATE_PROJECT,
} from "my-timesheet/graphql";
import { useOcSnackbar } from "oc/hooks";

type UseMyTimesheetProjectListParams = {
  page: number;
  pageSize: number;
  sort?: string;
  sortDirection?: "asc" | "desc";
  searchText?: string;
  searchField?: string;
  validity?: boolean;
};

export const useMyTimesheetProjectList = ({
  page,
  pageSize,
  sort,
  sortDirection,
  searchText,
  searchField,
  validity,
}: UseMyTimesheetProjectListParams) => {
  const { t } = useTranslation();
  const { setOpenMessage } = useOcSnackbar();

  const { data, loading, error, refetch } = useQuery(
    MY_TIMESHEET_GET_PROJECT_LIST,
    {
      fetchPolicy: "cache-and-network",
      variables: {
        page,
        pageSize,
        sort,
        sortDirection,
        searchText,
        searchField,
        validity,
      },
    },
  );

  useSubscription(MY_TIMESHEET_PROJECTS_SUBSCRIPTION, {
    onData: () => {
      refetch();
    },
  });

  const [createMySheetProjectMutation] = useMutation(
    MY_TIMESHEET_CREATE_PROJECT,
  );

  const createMySheetProject = useCallback(
    async (options?: any) => {
      try {
        const result = await createMySheetProjectMutation(options);
        setOpenMessage({
          type: "success",
          message: t("MY_TIMESHEET_NEW_PROJECT_SUCCESSFUL_CREATED"),
        });
        refetch();
        return result;
      } catch (error) {
        console.log({ error });
        setOpenMessage({
          type: "error",
          message: t("MY_TIMESHEET_NEW_PROJECT_CREATE_FAILED"),
        });
        refetch();
        throw error;
      }
    },
    [createMySheetProjectMutation, setOpenMessage, t, refetch],
  );

  const [updateMySheetProjectMutation] = useMutation(
    MY_TIMESHEET_UPDATE_PROJECT,
  );

  const updateMySheetProject = useCallback(
    async (options?: any) => {
      try {
        const result = await updateMySheetProjectMutation(options);
        setOpenMessage({
          type: "success",
          message: t("MY_TIMESHEET_PROJECT_SUCCESSFUL_UPDATED"),
        });
        refetch();
        return result;
      } catch (error) {
        console.log({ error });
        setOpenMessage({
          type: "error",
          message: t("MY_TIMESHEET_PROJECT_UPDATE_FAILED"),
        });
        refetch();
        throw error;
      }
    },
    [updateMySheetProjectMutation, setOpenMessage, t, refetch],
  );

  const list = data?.listMyTimesheetProjects || [];
  const rows = list?.rows || [];
  type RowType = {
    _id: string;
    [key: string]: any;
  };
  const parsedRows = (rows as RowType[]).map((row: RowType) => ({
    ...row,
    code: row._id,
  }));

  const newData = {
    ...list,
    rows: parsedRows,
  };

  return {
    data: newData,
    loading,
    error,
    createMySheetProject,
    updateMySheetProject,
  };
};

export default useMyTimesheetProjectList;
